import React, { useEffect, useState, useContext } from "react";
import ProductDetailVirtualGroupingSelect from "./ProductDetailVirtualGroupingSelect";
import ProductDetailVirtualGroupingTile from "./ProductDetailVirtualGroupingTile";
import {
  ConfigurationOption,
  ProductDetailGroupingSelect,
} from "../ProductDetailPageResult";
import ValidationError from "../Pricing/AddToCart/ValidationError";
import { getItemNumberFromUrl, getItemNumber } from "./VirtualGroupingHelper";
import { VirtualGroupingContext } from "../useVirtualGroupingContext";
import { User } from "~/common/Store/models/User";

interface VirtualGroupingProps {
  productGroupingSelect: ProductDetailGroupingSelect[];
  handleVirtualGroupingSelectChange: (itemNumber: string) => string;
  virtualGroupMemberToDisplay: string;
  addToCartError?: ValidationError;
  handleAddToCartError?: (validationError: ValidationError | undefined) => void;
  user: User;
  showPricingInDropdown?: boolean;
}

const VirtualGrouping = ({
  productGroupingSelect,
  handleVirtualGroupingSelectChange,
  virtualGroupMemberToDisplay,
  addToCartError,
  handleAddToCartError,
  user,
  showPricingInDropdown = true,
}: VirtualGroupingProps) => {
  const { virtualGroupOptionsMap } = useContext(VirtualGroupingContext);

  const [selectedOptions, setSelectedOption] = useState<ConfigurationOption[]>(
    []
  );

  // Preselect selected options when going directly to vg member
  useEffect(() => {
    if (virtualGroupMemberToDisplay) {
      const itemConfigs = productGroupingSelect.find(
        (variation) => variation.itemNumber === virtualGroupMemberToDisplay
      );
      if (itemConfigs) {
        setSelectedOption(itemConfigs.configurationOptions);
      }
    }

    /* When a hash link is clicked, the handlePopState function will run first,
    it will check that the popstate event was trigged from a hash link clicked because the state is null and return immediately.
    Then this handleHashChange method will run to save the itemNumber in the state for this hash change so that the back button
    will know what item number to set the active product. */
    const handleHashChange = (): void => {
      window.history.replaceState(
        { itemNumber: virtualGroupMemberToDisplay },
        ""
      );
    };

    const handlePopState = (ev: PopStateEvent) => {
      const itemNumber = getItemNumberFromUrl(ev);
      handleVirtualGroupingSelectChange(itemNumber || "");
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("hashchange", handleHashChange, false);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [
    handleVirtualGroupingSelectChange,
    productGroupingSelect,
    virtualGroupMemberToDisplay,
  ]);

  /* This function runs when either a vg selected dropdown is selected or a vg tile is clicked.
  It will update the url with the new item number and runs handleVirtualGroupingSelectChange function that is in the product detail index file.
  It updates everything in the product detail page. */
  const handleClickVirtualGroupingVariation = (
    name: string,
    value: string,
    validOption: boolean
  ) => {
    // Remove other selected options if an invalid option was selected
    if (!validOption) {
      setSelectedOption([{ name, value }]);
      return;
    }

    const newSelectedOptions = selectedOptions
      .filter((option) => option.name !== name)
      .concat({ name, value });

    if (newSelectedOptions.length === virtualGroupOptionsMap.length) {
      const itemNumber = getItemNumber(
        newSelectedOptions,
        productGroupingSelect
      );
      if (itemNumber) {
        const link = handleVirtualGroupingSelectChange(itemNumber);
        window.history.pushState({ itemNumber }, "", link);
      }

      if (handleAddToCartError) {
        handleAddToCartError(undefined);
      }
    }

    setSelectedOption(newSelectedOptions);
  };

  const includesMoreThanJustColorOption = productGroupingSelect?.[0]?.configurationOptions.find(
    (option) => option.name !== "Color"
  );

  return includesMoreThanJustColorOption ? (
    <ProductDetailVirtualGroupingSelect
      addToCartError={addToCartError}
      handleClickVirtualGroupingVariation={handleClickVirtualGroupingVariation}
      selectedOptions={selectedOptions}
      productGroupingSelect={productGroupingSelect}
      user={user}
      showPricingInDropdown={showPricingInDropdown}
    />
  ) : (
    <ProductDetailVirtualGroupingTile
      addToCartError={addToCartError}
      virtualGroupMemberToDisplay={virtualGroupMemberToDisplay}
      selectedOptions={selectedOptions}
      handleClickVirtualGroupingVariation={handleClickVirtualGroupingVariation}
    />
  );
};

export default VirtualGrouping;
